import { bizChatHandlers } from './bizChatHandlers';
import { logUsage, DatapointStatus, PerformanceDatapoint } from 'owa-analytics';
import type { IWebContentRequestMessage, CustomMessageResult } from '@metaos/hub-sdk';
import type { MailboxInfo } from 'owa-client-types';

/**
 * Core function to process messages from BizChat, either when OWA is hosting BizChat or when OWA is hosted by BizChat.
 *
 * @param message The message to process.
 * @param postMessage A function to post messages to the BizChat frame.
 * @param targetWindow The window to target for the message.
 * @returns The result of processing the message.
 */
export async function processBizChatMessage(
    mailboxInfo: MailboxInfo,
    { func, args }: IWebContentRequestMessage,
    postMessage: (message: any) => void,
    targetWindow?: Window,
    isImmersiveBizchat?: boolean
): Promise<CustomMessageResult> {
    const payload = args?.[0] ?? {};
    const scenario = payload?.scenario ?? 'n/a';

    logUsage('BizChatMessaging_MessageReceived', mailboxInfo, { func, scenario });

    const handler = bizChatHandlers[func];
    if (!handler) {
        const error = `Unhandled function: ${func}`;

        logUsage('BizChatMessaging_UnhandledFunction', mailboxInfo, { func, scenario });
        return { processed: true, data: { error } };
    }

    const dataPoint = new PerformanceDatapoint('BizChatMessaging_Handler', mailboxInfo);
    dataPoint.addCustomData({ func, scenario });
    try {
        const data = await handler(
            payload,
            postMessage,
            mailboxInfo,
            targetWindow,
            isImmersiveBizchat
        );
        dataPoint.end();
        return { processed: true, data };
    } catch (e: any) {
        dataPoint.endWithError(DatapointStatus.ClientError, e);
        return { processed: true, data: { error: Error(e).message } };
    }
}
